import { useState, useEffect, useCallback, useMemo } from 'preact/hooks';
import { TrendUp, TrendDown, ArrowsClockwise, Eye, Funnel, SquaresFour, Rows } from '@phosphor-icons/react';
import { api, type CoinMarket } from '../../services/api.js';
import { formatPrice, formatMarketCap } from '../../utils/format.js';
import { Skeleton } from '../shared/Skeleton.js';
import { useToast } from '../shared/Toast.js';
import { CoinRegistryModal } from './CoinRegistryModal.js';
import type { RatesSortKey, RatesSortDir, RatesFilter } from '../../utils/constants.js';

type RatesView = 'grid' | 'list';
const VIEW_KEY = 'cryptodash_rates_view';

function isMining(coin: CoinMarket): boolean {
  return ['xelis', 'xel'].includes(coin.id) || ['xelis', 'xel'].includes(coin.symbol);
}

export function RatesPage() {
  const [coins, setCoins] = useState<CoinMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRegistry, setShowRegistry] = useState(false);
  const [sortKey, setSortKey] = useState<RatesSortKey>('mcap');
  const [sortDir, setSortDir] = useState<RatesSortDir>('desc');
  const [filter, setFilter] = useState<RatesFilter>('all');
  const [view, setView] = useState<RatesView>(() =>
    (localStorage.getItem(VIEW_KEY) as RatesView) === 'list' ? 'list' : 'grid'
  );
  const { show: toast } = useToast();

  const changeView = (v: RatesView) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };

  const loadRates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getRates();
      setCoins(data);
    } catch {
      toast('Ошибка загрузки курсов', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadRates(); }, [loadRates]);

  const handleRegistrySaved = () => {
    setShowRegistry(false);
    loadRates();
    toast('Настройки сохранены', 'success');
  };

  const sorted = useMemo(() => {
    let list = [...coins];

    if (filter === 'rising') list = list.filter((c) => (c.price_change_percentage_24h ?? 0) > 0);
    if (filter === 'falling') list = list.filter((c) => (c.price_change_percentage_24h ?? 0) < 0);

    list.sort((a, b) => {
      let va: number, vb: number;
      switch (sortKey) {
        case 'name': return sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'price': va = a.current_price; vb = b.current_price; break;
        case 'change': va = a.price_change_percentage_24h ?? 0; vb = b.price_change_percentage_24h ?? 0; break;
        case 'mcap': va = a.market_cap ?? 0; vb = b.market_cap ?? 0; break;
        default: return 0;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });

    return list;
  }, [coins, sortKey, sortDir, filter]);

  const FILTERS: { id: RatesFilter; label: string }[] = [
    { id: 'all', label: 'Все' },
    { id: 'rising', label: 'Растущие' },
    { id: 'falling', label: 'Падающие' },
  ];

  return (
    <>
      <div class="rates-toolbar">
        <div class="seg-group" role="group" aria-label="Фильтр">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              class={`seg-btn${filter === f.id ? ' active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div class="rates-toolbar-end">
          <select
            class="setting-select"
            value={`${sortKey}-${sortDir}`}
            onChange={(e) => {
              const [k, d] = (e.target as HTMLSelectElement).value.split('-');
              setSortKey(k as RatesSortKey);
              setSortDir(d as RatesSortDir);
            }}
          >
            <option value="mcap-desc">Cap ↓</option>
            <option value="mcap-asc">Cap ↑</option>
            <option value="price-desc">Цена ↓</option>
            <option value="price-asc">Цена ↑</option>
            <option value="change-desc">Рост ↓</option>
            <option value="change-asc">Рост ↑</option>
            <option value="name-asc">Имя A-Z</option>
          </select>

          <div class="seg-group" role="group" aria-label="Вид">
            <button
              type="button"
              class={`seg-btn seg-icon${view === 'grid' ? ' active' : ''}`}
              onClick={() => changeView('grid')}
              title="Сетка"
              aria-label="Сетка"
            >
              <SquaresFour size={14} weight={view === 'grid' ? 'fill' : 'regular'} />
            </button>
            <button
              type="button"
              class={`seg-btn seg-icon${view === 'list' ? ' active' : ''}`}
              onClick={() => changeView('list')}
              title="Список"
              aria-label="Список"
            >
              <Rows size={14} weight={view === 'list' ? 'fill' : 'regular'} />
            </button>
          </div>

          <button class="btn btn-secondary" onClick={() => setShowRegistry(true)}>
            <Eye size={14} />
            <span class="btn-label">Настроить</span>
          </button>
          <button class="btn btn-secondary" onClick={loadRates}>
            <ArrowsClockwise size={14} />
            <span class="btn-label">Обновить</span>
          </button>
        </div>
      </div>

      {loading && coins.length === 0 ? (
        <div class="rates-grid">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} class="rate-card">
              <div class="rate-header">
                <Skeleton width="22px" height="22px" />
                <Skeleton width="55%" height="0.75rem" />
              </div>
              <Skeleton width="50%" height="1.25rem" />
            </div>
          ))}
        </div>
      ) : view === 'grid' ? (
        <div class="rates-grid">
          {sorted.map((coin) => <RateCard key={coin.id} coin={coin} />)}
        </div>
      ) : (
        <div class="rates-list">
          <div class="rates-list-head">
            <span />
            <span>Название</span>
            <span>Цена</span>
            <span>24ч</span>
            <span>Cap</span>
          </div>
          {sorted.map((coin) => <RateRow key={coin.id} coin={coin} />)}
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <div class="empty-state">
          <Funnel size={48} />
          <div class="empty-state-title">Нет монет</div>
          <div class="empty-state-message">Попробуйте изменить фильтр или добавить монеты через настройки</div>
        </div>
      )}

      {showRegistry && (
        <CoinRegistryModal
          onClose={() => setShowRegistry(false)}
          onSaved={handleRegistrySaved}
        />
      )}
    </>
  );
}

/**
 * Compact abbreviations applied only when the word is part of a longer name
 * (so standalone "Ethereum" stays, but "Ethereum Classic" → "ETH Classic").
 */
const NAME_ABBREVIATIONS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^official\s+/i, ''],             // "Official Trump" → "Trump"
  [/\bethereum\s+(?=\S)/gi, 'ETH '], // "Ethereum Classic" → "ETH Classic"
  [/\bbitcoin\s+(?=\S)/gi, 'BTC '],  // "Bitcoin Cash" → "BTC Cash"
];

/** Tidy a coin name for display: drop parenthetical notes, apply abbreviations. */
function cleanCoinName(name: string): string {
  let out = name.replace(/\s*\([^)]*\)/g, '').trim();
  for (const [pattern, replacement] of NAME_ABBREVIATIONS) {
    out = out.replace(pattern, replacement);
  }
  return out.trim() || name;
}

function RateCard({ coin }: { coin: CoinMarket }) {
  const change = coin.price_change_percentage_24h ?? 0;
  const isPositive = change >= 0;
  const mining = isMining(coin);

  return (
    <div class={`rate-card${mining ? ' is-mining' : ''}`}>
      <div class="rate-header">
        <img src={coin.image} alt="" class="coin-logo" loading="lazy" />
        <div class="rate-id">
          <span class="coin-name" title={coin.name}>{cleanCoinName(coin.name)}</span>
          <span class="coin-symbol">{coin.symbol.toUpperCase()}</span>
        </div>
      </div>
      <div class="rate-price">
        ${formatPrice(coin.current_price)}
        {coin.priceDivergencePct != null && (
          <span class="price-diverge-flag" title={`Источники цены расходятся на ${coin.priceDivergencePct}% (Bybit ↔ CoinGecko). Проверь символ/источник.`}>⚠</span>
        )}
      </div>
      <div class="rate-foot">
        <div class={`rate-change ${isPositive ? 'positive' : 'negative'}`}>
          {isPositive ? <TrendUp size={13} /> : <TrendDown size={13} />}
          <span>{Math.abs(change).toFixed(2)}%</span>
        </div>
        <span class="rate-mcap">${formatMarketCap(coin.market_cap)}</span>
      </div>
    </div>
  );
}

function RateRow({ coin }: { coin: CoinMarket }) {
  const change = coin.price_change_percentage_24h ?? 0;
  const isPositive = change >= 0;
  const mining = isMining(coin);

  return (
    <div class={`rate-row${mining ? ' is-mining' : ''}`}>
      <img src={coin.image} alt="" class="coin-logo" loading="lazy" />
      <div class="rate-id">
        <span class="coin-name" title={coin.name}>{cleanCoinName(coin.name)}</span>
        <span class="coin-symbol">{coin.symbol.toUpperCase()}</span>
      </div>
      <span class="rate-row-price">
        ${formatPrice(coin.current_price)}
        {coin.priceDivergencePct != null && (
          <span class="price-diverge-flag" title={`Источники цены расходятся на ${coin.priceDivergencePct}% (Bybit ↔ CoinGecko). Проверь символ/источник.`}>⚠</span>
        )}
      </span>
      <div class={`rate-change ${isPositive ? 'positive' : 'negative'}`}>
        {isPositive ? <TrendUp size={13} /> : <TrendDown size={13} />}
        <span>{Math.abs(change).toFixed(2)}%</span>
      </div>
      <span class="rate-row-mcap">${formatMarketCap(coin.market_cap)}</span>
    </div>
  );
}
